import {
  CAR_ACCEL, CAR_BRAKE, CAR_FRICTION,
  CAR_MAX_SPEED, CAR_MAX_REVERSE,
  STEER_MAX_ANGLE, STEER_RATE, STEER_RETURN,
  CAR_RADIUS,
  FLY_ACCEL, FLY_MAX_VSPEED, FLY_DRAG,
} from '../constants.js';

/**
 * Arcade car physics with collision detection and optional airplane mode.
 *
 * Coordinate convention (matches Three.js scene):
 *   angle = 0  →  facing +Z (south)
 *   angle increases CCW from above (+Y)
 */
export class Car {
  constructor() {
    this.x          = 0;
    this.z          = 0;
    this.y          = 0;      // altitude above terrain (0 when grounded)
    this.angle      = 0;
    this.speed      = 0;
    this.wheelAngle = 0;
    this.wheelRot   = 0;
    this.elevFn     = null;
    this.maxSpeed   = CAR_MAX_SPEED;
    this.flying     = false;  // airplane mode
    this.altSpeed   = 0;      // vertical speed (m/s)
    this.collidables = [];    // [{ax,az,bx,bz}] wall segments from main.js
    this.noClip      = true;  // default: pass through objects; set false to enable collisions
    this.odometer    = 0;     // total distance driven in metres
  }

  reset(x, z, angle = 0) {
    this.x          = x;
    this.z          = z;
    this.y          = 0;
    this.angle      = angle;
    this.speed      = 0;
    this.wheelAngle = 0;
    this.wheelRot   = 0;
    this.flying     = false;
    this.altSpeed   = 0;
  }

  /** Physics step. Call once per frame with the elapsed time (seconds). */
  update(dt, input) {
    // 1. Throttle / brake
    if (input.forward) {
      this.speed += CAR_ACCEL * dt;
    } else if (input.backward) {
      if (this.speed > 0.5) {
        this.speed -= CAR_BRAKE * dt;
      } else {
        this.speed -= CAR_ACCEL * 0.7 * dt;
      }
    }

    // 2. Friction
    this.speed *= Math.exp(-CAR_FRICTION * dt);

    // 3. Clamp speed
    this.speed = Math.max(CAR_MAX_REVERSE, Math.min(this.maxSpeed, this.speed));

    // 4. Dead-zone
    if (Math.abs(this.speed) < 0.04) this.speed = 0;

    // 5. Steering
    const targetWheelAngle = input.left  ?  STEER_MAX_ANGLE
                           : input.right ? -STEER_MAX_ANGLE
                           : 0;
    const delta    = targetWheelAngle - this.wheelAngle;
    const rate     = input.left || input.right ? STEER_RATE : STEER_RETURN;
    const maxDelta = rate * dt;
    this.wheelAngle += Math.sign(delta) * Math.min(Math.abs(delta), maxDelta);

    // 6. Yaw
    const speedNorm = Math.abs(this.speed) / this.maxSpeed;
    const turnRate  = this.wheelAngle * 2.0 * Math.min(speedNorm, 1);
    const dir       = this.speed >= 0 ? 1 : -1;
    this.angle     += turnRate * dir * dt;

    // 7. Slope gravity (grounded only)
    if (!this.flying && this.elevFn) {
      const sinA  = Math.sin(this.angle);
      const cosA  = Math.cos(this.angle);
      const PROBE = 3;
      const hFront = this.elevFn(this.x + sinA * PROBE, this.z + cosA * PROBE);
      const hBack  = this.elevFn(this.x - sinA * PROBE, this.z - cosA * PROBE);
      const slope  = (hFront - hBack) / (PROBE * 2);
      this.speed  -= 9.81 * slope * dt;
    }

    // 8. Horizontal position integration
    this.x += Math.sin(this.angle) * this.speed * dt;
    this.z += Math.cos(this.angle) * this.speed * dt;

    // 9. Collision response (grounded only; skip when flying or noClip)
    if (!this.flying && !this.noClip) this._resolveCollisions();

    // 10. Airplane vertical movement
    if (this.flying) {
      if (input.flyUp)   this.altSpeed = Math.min( FLY_MAX_VSPEED, this.altSpeed + FLY_ACCEL * dt);
      if (input.flyDown) this.altSpeed = Math.max(-FLY_MAX_VSPEED, this.altSpeed - FLY_ACCEL * dt);
      if (!input.flyUp && !input.flyDown) {
        this.altSpeed *= Math.exp(-FLY_DRAG * dt);
      }
      this.y += this.altSpeed * dt;
      if (this.y < 0) { this.y = 0; this.altSpeed = 0; }
    }

    // 11. Wheel spin animation
    const WHEEL_RADIUS = 0.35;
    this.wheelRot += (this.speed * dt) / WHEEL_RADIUS;

    // 12. Odometer — accumulate total distance driven
    this.odometer += Math.abs(this.speed) * dt;
  }

  /** Resolve penetration against collidable wall segments. */
  _resolveCollisions() {
    const MAX_CHECK = 30; // metres — skip segments farther than this

    for (const seg of this.collidables) {
      // Quick bounding-box cull
      const midX = (seg.ax + seg.bx) * 0.5;
      const midZ = (seg.az + seg.bz) * 0.5;
      if (Math.abs(midX - this.x) > MAX_CHECK ||
          Math.abs(midZ - this.z) > MAX_CHECK) continue;

      // Closest point on segment to car centre
      const dx  = seg.bx - seg.ax, dz = seg.bz - seg.az;
      const len2 = dx * dx + dz * dz;
      if (len2 < 0.01) continue;

      const t  = Math.max(0, Math.min(1, ((this.x - seg.ax) * dx + (this.z - seg.az) * dz) / len2));
      const cx = seg.ax + t * dx;
      const cz = seg.az + t * dz;
      const ex = this.x - cx, ez = this.z - cz;
      const dist = Math.sqrt(ex * ex + ez * ez);

      if (dist < CAR_RADIUS && dist > 0.001) {
        // Push car out along normal
        const nx = ex / dist, nz = ez / dist;
        this.x = cx + nx * CAR_RADIUS;
        this.z = cz + nz * CAR_RADIUS;
        // Fully inelastic: kill velocity component into the wall (no bounce)
        const vn = (Math.sin(this.angle) * nx + Math.cos(this.angle) * nz) * this.speed;
        if (vn < 0) this.speed -= vn;
      }
    }
  }

  get state() {
    return {
      x:          this.x,
      z:          this.z,
      y:          this.y,
      angle:      this.angle,
      speed:      this.speed,
      wheelAngle: this.wheelAngle,
      wheelRot:   this.wheelRot,
      flying:     this.flying,
      odometer:   this.odometer,
    };
  }
}
